import { useEffect, useRef, type ReactNode } from "react";
import { LoadingOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation } from "../../../../api/reservations";
import { CloseIcon } from "../../../../components/restaurant-schema/schemaIcons";
import { formatClock } from "../../logic/dayTime";
import type { NewReservationForm } from "../../hooks/useNewReservationForm";
import { useReservationFormat } from "../../hooks/useReservationFormat";
import { useEscapeBlurOrClose } from "../../hooks/useEscapeBlurOrClose";
import { useSaveReservation } from "../../hooks/useSaveReservation";
import WhenSection from "./WhenSection";
import GuestsDurationSection from "./GuestsDurationSection";
import TablesSection from "./TablesSection";
import GuestSection from "./GuestSection";

/**
 * Дясната колона в режим „Нова резервация" (или „Редакция на резервация",
 * когато `form.editing` е зададена): хедър (заглавие, „Днес · 19:15–21:15 ·
 * 2 гости", ×), скрол с КОГА / ГОСТИ · ПРОДЪЛЖИТЕЛНОСТ / МАСИ / ГОСТ и футър
 * „Отказ" + „Създай резервация" / „Запази промените" (POST или PATCH). Esc
 * затваря (preventDefault).
 * Масите се избират върху платното (режимът избор) — състоянието е във
 * `form`, общо с него; на телефон — в самата секция „Маси" (`tablePicker`).
 */
const NewReservationPanel = ({
  form,
  todayKey,
  nowMs,
  isNarrow,
  stacked,
  onSuggest,
  onClose,
  onSaved,
  tablePicker,
}: {
  form: NewReservationForm;
  todayKey: string;
  nowMs: number;
  isNarrow: boolean;
  // Колоната е под платното (тесен екран/таблет): без вътрешен скрол.
  stacked: boolean;
  onSuggest: () => void;
  onClose: () => void;
  // След успешно създаване/редакция — резервацията, както я върна BE.
  onSaved: (reservation: Reservation) => void;
  // Изборът на маса на телефон (без схема); null — масите се избират на схемата.
  tablePicker: ReactNode;
}) => {
  const { t } = useTranslation();
  const format = useReservationFormat();
  const panelRef = useRef<HTMLDivElement>(null);
  const { draft, editing } = form;
  const { save: handleSave, isSaving } = useSaveReservation(form, onSaved);

  useEscapeBlurOrClose(onClose);

  // Под платното панелът се показва веднага (иначе е под сгъвката).
  useEffect(() => {
    if (stacked) panelRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [stacked]);

  const subtitle = [
    format.relativeDay(draft.slot.dateKey, todayKey),
    `${formatClock(form.fromMs)}–${formatClock(form.toMs)}`,
    format.guests(draft.guests),
  ].join(" · ");

  return (
    <div
      ref={panelRef}
      className={`flex flex-col rounded-[14px] border border-shade-2 bg-surface ${
        stacked ? "scroll-mt-3" : "min-h-0 flex-1 overflow-hidden"
      }`}
    >
      <div className="flex shrink-0 items-start justify-between gap-2 border-b border-shade-2 px-4 py-3.5">
        <div className="min-w-0">
          <div className="text-[16px] font-bold leading-[1.2] tracking-[-0.3px] text-ink">
            {t(editing ? "reservations.form.editTitle" : "reservations.form.title")}
          </div>
          <div className="mt-1 truncate text-[12.5px] font-medium tabular-nums text-body">{subtitle}</div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label={t("reservations.form.close")}
          className="flex size-[30px] shrink-0 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-muted transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          <CloseIcon className="size-3.5" />
        </button>
      </div>

      <div className={`flex flex-col gap-5 px-4 py-4 ${stacked ? "" : "min-h-0 flex-1 overflow-y-auto"}`}>
        <WhenSection
          slot={draft.slot}
          todayKey={todayKey}
          nowMs={nowMs}
          slotPast={form.slotPast}
          slotOutsideHours={form.slotOutsideHours}
          originalSlot={form.originalSlot}
          onDayChange={form.setDay}
          onHourChange={form.setHour}
          onMinuteChange={form.setMinute}
        />
        <GuestsDurationSection
          guests={draft.guests}
          durationMinutes={draft.durationMinutes}
          onGuestsChange={form.setGuests}
          onDurationChange={form.setDuration}
        />
        <TablesSection
          selectedTables={form.selectedTables}
          busySelectedTables={form.busySelectedTables}
          guests={draft.guests}
          seats={form.seats}
          hasEnoughSeats={form.hasEnoughSeats}
          dayState={form.dayError ? "error" : form.isLoadingDay ? "loading" : "ready"}
          onRetryDay={form.retryDay}
          onSuggest={onSuggest}
          onRemove={form.toggleTable}
          picker={tablePicker}
        />
        <GuestSection
          draft={draft}
          errors={form.guestErrors}
          isNarrow={isNarrow}
          guestHistory={form.guestHistory}
          onFieldChange={form.setField}
          onFieldBlur={form.touchField}
        />
      </div>

      <div
        className={`flex shrink-0 gap-2 border-t border-shade-2 bg-surface px-4 py-3 ${
          stacked ? "sticky bottom-0 rounded-b-[14px] pb-[calc(12px+env(safe-area-inset-bottom,0px)*0.5)]" : ""
        }`}
      >
        <button
          type="button"
          onClick={onClose}
          className="h-11 shrink-0 cursor-pointer rounded-[10px] border border-shade-3 bg-transparent px-4 text-[14px] font-semibold text-body transition-colors duration-150 hover:bg-shade-1 hover:text-ink"
        >
          {t("reservations.form.cancel")}
        </button>
        <button
          type="button"
          onClick={() => void handleSave()}
          // Не `disabled`: кликът по изключения бутон показва грешките.
          aria-disabled={!form.canSubmit || isSaving}
          className={`flex h-11 flex-1 items-center justify-center gap-2 rounded-[10px] border-none bg-brand text-[14px] font-bold text-white transition-colors duration-150 ${
            form.canSubmit && !isSaving ? "cursor-pointer hover:bg-brand-deep" : "cursor-not-allowed opacity-45"
          }`}
        >
          {isSaving && <LoadingOutlined />}
          {t(editing ? "reservations.form.save" : "reservations.form.create")}
        </button>
      </div>
    </div>
  );
};

export default NewReservationPanel;
